import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";

import { RootState, StatementsState } from "@/types/store";
import { getStatements, getMoreStatements } from "@/api/statements/thunks";
import { Statement } from "@/api/statements/types";

/**
 * Initial state for the statements slice.
 * Contains empty statements array and default filter values.
 */
const initialState: StatementsState = {
  statements: [],
  loading: false,
  error: null,
  page: 1,
  hasMore: true,
  filters: {
    propertyId: null,
    year: null,
    month: null,
  },
};

/**
 * Redux slice for managing month-end statements state.
 * Handles fetching, filtering, and pagination of statements.
 */
export const StatementsSlice = createSlice({
  name: "StatementsSlice",
  initialState,
  reducers: {
    /**
     * Updates the filter values for statements.
     * Partial updates are supported - only provided fields will be changed.
     */
    setFilters: (
      state,
      action: PayloadAction<{
        propertyId?: string | null;
        year?: number | null;
        month?: number | null;
      }>
    ) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    /**
     * Resets the statements list and pagination state.
     * Used when filters change to fetch fresh data.
     */
    resetStatements: (state) => {
      state.statements = [];
      state.page = 1;
      state.hasMore = true;
      state.error = null;
    },
    /**
     * Clears all filter values back to their defaults.
     */
    clearFilters: (state) => {
      state.filters = {
        propertyId: null,
        year: null,
        month: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle initial fetch
      .addCase(getStatements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getStatements.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.statements = payload.statements;
        state.page = payload.page;
        state.hasMore = payload.hasMore;
      })
      .addCase(getStatements.rejected, (state, action) => {
        state.loading = false;
        // Handle new error format with message and status
        const errorPayload = action.payload as { message?: string; status?: number } | string;
        if (typeof errorPayload === "object" && errorPayload?.message) {
          state.error = errorPayload.message;
        } else {
          state.error = errorPayload as string;
        }
      })
      // Handle pagination (load more)
      .addCase(getMoreStatements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMoreStatements.fulfilled, (state, { payload }) => {
        state.loading = false;
        // Filter out duplicates to prevent duplicate keys in the UI
        const newStatements = payload.statements.filter(
          (newStatement: Statement) =>
            !state.statements.some(
              (existingStatement) => existingStatement.id === newStatement.id
            )
        );
        state.statements = [...state.statements, ...newStatements];
        state.page = payload.page;
        state.hasMore = payload.hasMore;
      })
      .addCase(getMoreStatements.rejected, (state, action) => {
        state.loading = false;
        // Handle new error format with message and status
        const errorPayload = action.payload as { message?: string; status?: number } | string;
        if (typeof errorPayload === "object" && errorPayload?.message) {
          state.error = errorPayload.message;
        } else {
          state.error = errorPayload as string;
        }
      });
  },
});

export const { setFilters, resetStatements, clearFilters } =
  StatementsSlice.actions;

export default StatementsSlice.reducer;

/**
 * Custom hook for accessing statements state from Redux store.
 * Returns all statement-related state including data, loading, error, and filters.
 *
 * @returns Object containing statements array, loading state, error, page, hasMore, and filters
 *
 * @example
 * ```tsx
 * const { statements, loading, filters } = useStatementsData();
 * ```
 */
export const useStatementsData = () => {
  const statements = useSelector(
    (state: RootState) => state.Statements.statements
  );
  const loading = useSelector((state: RootState) => state.Statements.loading);
  const error = useSelector((state: RootState) => state.Statements.error);
  const page = useSelector((state: RootState) => state.Statements.page);
  const hasMore = useSelector((state: RootState) => state.Statements.hasMore);
  const filters = useSelector((state: RootState) => state.Statements.filters);

  return {
    statements,
    loading,
    error,
    page,
    hasMore,
    filters,
  };
};
